import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import Select from 'react-select';
import Modal from '../General/Modal';
import { getTags } from '../../actions/tagActions';
import './AudienceModal.scss';

const SEARCH_DEBOUNCE_MS = 200;
const MAX_OPTIONS_IN_DROPDOWN = 50;

const createSelectionBox = id => ({ id, tags: [] });

const FilterGroupBox = ({ box, index, isActive, canRemove, onSetActive, onRemoveTag, onRemoveBox }) => (
  <div
    className={`audience-modal-selected-box ${isActive ? 'is-active' : ''}`}
    onClick={onSetActive}
    role='button'
    tabIndex={0}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSetActive();
      }
    }}
  >
    <div className='audience-modal-selected-box-header'>
      <span className='audience-modal-selected-box-title'>Filter Group {index + 1}</span>
      {canRemove && (
        <button
          type='button'
          className='audience-modal-selected-box-remove'
          onClick={e => {
            e.stopPropagation();
            onRemoveBox();
          }}
          aria-label='Remove filter group'
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
    {box.tags.length === 0 ? (
      <div className='audience-modal-selected-empty'>No tags selected</div>
    ) : (
      <div className='audience-modal-selected-list' onClick={e => e.stopPropagation()}>
        {box.tags.map(opt => (
          <div key={opt.value} className='audience-modal-pill'>
            <span className='audience-modal-pill-label'>{opt.label}</span>
            <button type='button' className='audience-modal-pill-remove' onClick={() => onRemoveTag(opt.value)} aria-label={`Remove ${opt.label}`}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

FilterGroupBox.propTypes = {
  box: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    tags: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onSetActive: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func.isRequired,
  onRemoveBox: PropTypes.func.isRequired
};

const AudienceModal = ({ isOpen, onClose, onSelect }) => {
  const dispatch = useDispatch();
  const tags = useSelector(state => state.tags?.tags ?? []);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterGroupBoxes, setFilterGroupBoxes] = useState(() => [createSelectionBox(1)]);
  const [activeBoxId, setActiveBoxId] = useState(1);
  const debounceRef = useRef(null);
  const nextBoxIdRef = useRef(2);

  useEffect(() => {
    if (!isOpen) return;
    setTagsLoading(true);
    dispatch(getTags()).finally(() => setTagsLoading(false));
    setSearchInput('');
    setDebouncedSearch('');
    setFilterGroupBoxes([createSelectionBox(1)]);
    setActiveBoxId(1);
    nextBoxIdRef.current = 2;
  }, [isOpen, dispatch]);

  // Debounce search so we don't filter tens of thousands of tags on every keystroke
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      debounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const tagOptions = useMemo(
    () =>
      (tags || []).map(tag => {
        const tagLabel = tag.value || tag.name || `Tag ${tag.id}`;
        const type = tag.type || 'general';
        return {
          value: tag.id,
          label: `${tagLabel} (${type})`,
          tagType: type,
          raw: tag
        };
      }),
    [tags]
  );

  const filteredOptions = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return [];
    const filtered = tagOptions.filter(opt => opt.label.toLowerCase().includes(q) || (opt.tagType && opt.tagType.toLowerCase().includes(q)));
    return filtered.slice(0, MAX_OPTIONS_IN_DROPDOWN);
  }, [tagOptions, debouncedSearch]);

  const activeBox = filterGroupBoxes.find(b => b.id === activeBoxId);

  const handleSelectOption = opt => {
    if (!opt || !activeBox) return;
    const boxTags = activeBox.tags;
    const already = boxTags.some(s => s.value === opt.value);
    if (!already) {
      setFilterGroupBoxes(prev => prev.map(b => (b.id === activeBoxId ? { ...b, tags: [...b.tags, opt] } : b)));
    }
    setSearchInput('');
  };

  const removeTagFromBox = (boxId, value) => {
    setFilterGroupBoxes(prev => prev.map(b => (b.id === boxId ? { ...b, tags: b.tags.filter(t => t.value !== value) } : b)));
  };

  const addSelectionBox = () => {
    const id = nextBoxIdRef.current++;
    setFilterGroupBoxes(prev => [...prev, createSelectionBox(id)]);
    setActiveBoxId(id);
  };

  const removeBox = boxId => {
    setFilterGroupBoxes(prev => {
      const next = prev.filter(b => b.id !== boxId);
      if (next.length === 0) return prev;
      if (activeBoxId === boxId) {
        setActiveBoxId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const handleDone = () => {
    const idGroups = filterGroupBoxes.map(box => ({
      tagIds: box.tags.map(t => t.value),
      labels: box.tags.map(t => t.label)
    }));
    onSelect(idGroups);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal close={onClose} title='Select audience'>
      <div className='audience-modal-outer'>
        <div className='audience-modal-search'>
          <Select
            className='audience-modal-select'
            classNamePrefix='audience-select'
            placeholder='Search by tag'
            value={null}
            inputValue={searchInput}
            onInputChange={setSearchInput}
            onChange={handleSelectOption}
            options={filteredOptions}
            filterOption={() => true}
            isClearable
            isLoading={tagsLoading}
            noOptionsMessage={() => (debouncedSearch.trim() ? 'No matching tags' : 'Type to search tags')}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        {filterGroupBoxes.map((box, index) => (
          <React.Fragment key={box.id}>
            <FilterGroupBox
              box={box}
              index={index}
              isActive={box.id === activeBoxId}
              canRemove={filterGroupBoxes.length > 1}
              onSetActive={() => setActiveBoxId(box.id)}
              onRemoveTag={value => removeTagFromBox(box.id, value)}
              onRemoveBox={() => removeBox(box.id)}
            />
            {index < filterGroupBoxes.length - 1 && <div className='audience-modal-or-label'>OR</div>}
          </React.Fragment>
        ))}
        <button type='button' className='audience-modal-add-box-btn' onClick={addSelectionBox}>
          Add selection box
        </button>
        <div className='audience-modal-actions'>
          <button type='button' className='audience-modal-btn audience-modal-btn-primary' onClick={handleDone}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
};

AudienceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default AudienceModal;
